import PropTypes from "prop-types";
import { useUndoKey } from "src/utils/lastUndo";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useInView } from "react-intersection-observer";
import { Link as RouterLink } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Box,
  ButtonBase,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Link,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { IconAlertTriangle, IconCheck, IconEye, IconFileOff, IconFileText, IconMail, IconPhoto, IconRefresh, IconShieldCheck, IconX } from "@tabler/icons";
import { casesApi, openPrivateFile } from "./api";
import { DownloadButton, FilePreviewPane, fileName, useFilePreview, viewerGate } from "./FilePreview";
import { answerForReading, dateLong, dayOfInstant } from "./format";
import ActionButton, { iconButtonSx } from "src/components/shared/ActionButton";
import { docAnchor, hasModifier, readLetterKeys, saveLetterKeys } from "./workspace";
import { aiCheckView, isPassportDocument } from "./rules";
import { askedBecause } from "src/pages/catalogue/conditions";
import { COPY_TEXT, validityText } from "src/pages/catalogue/WhoIsAsked";

// What the customer typed about their passport, shown beside its copy so the
// agent compares without leaving the viewer.
const PASSPORT_KEYS = [
  "surname",
  "given_names",
  "date_of_birth",
  "sex",
  "nationality",
  "passport_number",
  "passport_issue_date",
  "passport_expiry_date",
];

function passportFacts(traveller) {
  const fields = traveller.sections.flatMap((s) => s.fields);
  return PASSPORT_KEYS.map((key) => fields.find((f) => f.key === key))
    .filter((f) => f && f.value)
    .map((f) => ({ ...f, value: answerForReading(f.value, f.type || (/date/.test(f.key) ? "date" : null)) }));
}

// The two states of a new copy, named the same everywhere (QA nadia-15):
// marked and not sent to the customer yet, or asked and waited on.
export function newCopyWords(upload, partner) {
  if (upload?.review_status !== "needs_new") return null;
  return upload.requested_at
    ? `New copy asked on ${dayOfInstant(upload.requested_at)}, waiting for ${partner ? partner : "customer"}`
    : "New copy needed, not sent yet";
}

// "Approved Personal bank statement (Zz Tester) · Undo" after every
// decision, from a button, a shortcut or the viewer (QA nadia-11).
export function UndoToast({ text, onUndo, closeToast }) {
  // Ctrl+Z presses it too, for a minute (georges-09).
  useUndoKey(() => {
    closeToast?.();
    return onUndo();
  });
  return (
    <Stack direction="row" alignItems="center" spacing={1} justifyContent="space-between">
      <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
        {text}
      </Typography>
      <ActionButton
        variant="text"
        sx={{ flexShrink: 0 }}
        aria-keyshortcuts="Control+Z"
        onClick={async () => {
          closeToast?.();
          await onUndo();
        }}
      >
        Undo
      </ActionButton>
    </Stack>
  );
}

UndoToast.propTypes = { text: PropTypes.string.isRequired, onUndo: PropTypes.func.isRequired, closeToast: PropTypes.func };

// Reasons agents give most, one tap each; the text stays editable.
const QUICK_REASONS = [
  "The photo is blurry or cut off",
  "It has expired or expires too soon",
  "This is not the right document",
  "It is older than allowed",
  "A page is missing",
];

const IMAGE_FILE = /\.(jpe?g|png|gif|webp|bmp)$/i;
const extension = (file) => (/\.([a-z0-9]{2,5})$/i.exec(file || "")?.[1] || "file").toUpperCase();

// The full document, large. Approve or ask for a new copy from here too.
// Approve waits until the agent has actually seen the document (QA F01).
export function FileViewer({ file, title, preloaded, onClose, onApprove, approveLabel = "Approve", onAskNew, onEmail, emailLabel, facts }) {
  const preview = useFilePreview(file, preloaded);
  const [downloaded, setDownloaded] = useState(false);
  const gate = viewerGate({ shown: preview.shown, downloaded, failed: preview.failed, kind: preview.kind });
  const markDownloaded = () => setDownloaded(true);
  // Focus starts on Close (not the dialog's wrapper) and moves to Approve as
  // soon as the document is showing, unless the agent has moved on (QA F34).
  const closeRef = useRef(null);
  const approveRef = useRef(null);
  useEffect(() => {
    if (gate.canApprove && document.activeElement === closeRef.current) approveRef.current?.focus();
  }, [gate.canApprove]);

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth aria-labelledby="case-viewer-title">
      <DialogTitle id="case-viewer-title" sx={{ display: "flex", alignItems: "center", gap: 1, pr: 1.5 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="h6" component="span" noWrap sx={{ display: "block" }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {fileName(file)}
          </Typography>
        </Box>
        <IconButton aria-label="Close" onClick={onClose} sx={iconButtonSx} ref={closeRef} autoFocus>
          <IconX size={20} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, display: "flex", flexDirection: { xs: "column", md: "row" }, bgcolor: "action.hover" }}>
        <Box sx={{ flex: 1, minWidth: 0, minHeight: 320, p: 2, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <FilePreviewPane file={file} title={title} preview={preview} onDownloaded={markDownloaded} />
        </Box>
        {facts?.length > 0 && (
          <Box
            component="aside"
            aria-label="The customer’s passport answers"
            sx={{
              width: { md: 280 },
              flexShrink: 0,
              p: 2,
              borderRadius: 0,
              bgcolor: "background.paper",
              borderLeft: { md: 1 },
              borderTop: { xs: 1, md: 0 },
              borderColor: "divider",
            }}
          >
            <Typography variant="subtitle2" component="h3" mb={1}>
              What the customer answered
            </Typography>
            <Box component="dl" sx={{ m: 0, display: "grid", gap: 1 }}>
              {facts.map((f) => (
                <Box key={f.question_id}>
                  <Typography component="dt" variant="caption" color="text.secondary">
                    {f.label}
                  </Typography>
                  <Typography component="dd" sx={{ m: 0, fontWeight: 600, fontFamily: "monospace", wordBreak: "break-word" }}>
                    {f.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1, flexWrap: "wrap" }}>
        <DownloadButton file={file} url={preview.url} onDownloaded={markDownloaded} />
        <Box sx={{ flex: 1 }} />
        {onEmail && (
          <ActionButton onClick={onEmail} startIcon={<IconMail size={16} />}>
            {emailLabel}
          </ActionButton>
        )}
        {onAskNew && (
          <ActionButton onClick={onAskNew} startIcon={<IconRefresh size={16} />}>
            Ask for a new copy
          </ActionButton>
        )}
        {onApprove ? (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap justifyContent="flex-end">
            {!gate.canApprove && (
              <Typography id="case-viewer-approve-why" variant="body2" color="text.secondary">
                {gate.why}
              </Typography>
            )}
            <ActionButton
              ref={approveRef}
              variant="contained"
              onClick={onApprove}
              disabled={!gate.canApprove}
              aria-describedby={gate.canApprove ? undefined : "case-viewer-approve-why"}
              busyLabel="Approving…"
              startIcon={<IconCheck size={16} />}
            >
              {approveLabel}
            </ActionButton>
          </Stack>
        ) : (
          <ActionButton onClick={onClose}>Close</ActionButton>
        )}
      </DialogActions>
    </Dialog>
  );
}

FileViewer.propTypes = {
  file: PropTypes.string.isRequired,
  title: PropTypes.string,
  preloaded: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onApprove: PropTypes.func,
  approveLabel: PropTypes.string,
  onAskNew: PropTypes.func,
  onEmail: PropTypes.func,
  emailLabel: PropTypes.string,
  facts: PropTypes.array,
};

// A chip never shrinks to one letter (QA nadia-05): a long one wraps.
const chipSx = { flexShrink: 0, maxWidth: "100%", height: "auto", minHeight: 24, "& .MuiChip-label": { whiteSpace: "normal", py: 0.25 } };

function ReviewChip({ upload, partner }) {
  if (!upload) return <Chip size="small" variant="outlined" label="Not uploaded" sx={chipSx} />;
  if (upload.review_status === "approved") {
    return <Chip size="small" color="success" label={upload.reviewed_by ? `Approved by ${upload.reviewed_by}` : "Approved"} sx={chipSx} />;
  }
  if (upload.review_status === "needs_new") return <Chip size="small" color="warning" label={newCopyWords(upload, partner)} sx={chipSx} />;
  return <Chip size="small" color="primary" variant="outlined" label="To review" sx={chipSx} />;
}

ReviewChip.propTypes = { upload: PropTypes.object, partner: PropTypes.string };

// A small preview: the image itself once fetched, the file type otherwise.
function Thumbnail({ upload, preview, name, onOpen }) {
  const box = {
    width: { xs: 48, sm: 56 },
    height: { xs: 48, sm: 56 },
    flexShrink: 0,
    borderRadius: 1.5,
    border: 1,
    borderColor: "divider",
    bgcolor: "grey.100",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "text.secondary",
  };
  if (!upload) {
    return (
      <Box sx={{ ...box, borderStyle: "dashed", bgcolor: "transparent" }} aria-hidden>
        <IconFileOff size={20} />
      </Box>
    );
  }
  const image = IMAGE_FILE.test(upload.file) && !preview.notImage;
  let content;
  if (image && preview.url) content = <img src={preview.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />;
  else if (image && preview.failed) content = <IconPhoto size={20} />;
  else if (image) content = <Skeleton variant="rectangular" width="100%" height="100%" />;
  else
    content = (
      <Stack alignItems="center" spacing={0.25}>
        <IconFileText size={20} />
        <Typography sx={{ fontSize: 10, fontWeight: 700, lineHeight: 1 }}>{extension(upload.file)}</Typography>
      </Stack>
    );
  return (
    <ButtonBase
      onClick={onOpen}
      tabIndex={-1}
      aria-label={`View ${name}`}
      title={preview.failed ? "The preview did not load. Open the document to try again." : undefined}
      sx={{ ...box, "&:hover": { borderColor: "primary.main" } }}
    >
      {content}
    </ButtonBase>
  );
}

Thumbnail.propTypes = { upload: PropTypes.object, preview: PropTypes.object, name: PropTypes.string, onOpen: PropTypes.func };

// Moves focus to the previous or next document row.
// What the AI document check found (rules.aiCheckView): one quiet line when
// it looks right, else what might be wrong, its note for the agent, and a
// typed expiry that isn't the printed one. Never a decision.
function AiCheckNote({ view }) {
  if (view.ok) {
    return (
      <Typography variant="body2" color="success.dark" sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5, ml: { sm: 8.5 } }}>
        <IconShieldCheck size={15} aria-hidden="true" /> {view.label}
      </Typography>
    );
  }
  return (
    <Box sx={{ mt: 1, px: 1.5, py: 1, borderRadius: 2, bgcolor: "warning.light", ml: { sm: 8.5 } }}>
      <Typography variant="body2" fontWeight={600} sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "warning.dark" }}>
        <IconAlertTriangle size={15} aria-hidden="true" /> {view.label}
      </Typography>
      {view.issues.length > 0 && (
        <Box component="ul" sx={{ m: 0, mt: 0.25, pl: 2.5 }}>
          {view.issues.map((text) => (
            <Typography component="li" variant="body2" key={text}>
              {text}
            </Typography>
          ))}
        </Box>
      )}
      {view.expiry && (
        <Typography variant="body2">
          The document shows it expires {dateLong(view.expiry.printed)}; the customer typed {dateLong(view.expiry.typed)}.
        </Typography>
      )}
      {view.note && (
        <Typography variant="body2" color="text.secondary">
          {view.note}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary">
        Not a decision: open the file and check it yourself.
      </Typography>
    </Box>
  );
}

AiCheckNote.propTypes = {
  view: PropTypes.shape({
    ok: PropTypes.bool,
    label: PropTypes.string,
    issues: PropTypes.arrayOf(PropTypes.string),
    note: PropTypes.string,
    expiry: PropTypes.shape({ printed: PropTypes.string, typed: PropTypes.string }),
  }).isRequired,
};

function focusSibling(row, step) {
  const rows = [...(row.closest("[data-doc-list]")?.querySelectorAll("[data-doc-row]") || [])];
  rows[rows.indexOf(row) + step]?.focus();
}

// What a decision did, for its toast: "Approved Passport First Page (Zz Tester)".
function decisionWords(decision, name) {
  if (decision === "approved") return `Approved ${name}`;
  if (decision === "needs_new") return `${name}: new copy needed, not sent yet`;
  return `Back to “To review”: ${name}`;
}

// The review an undo goes back to: [decision, reason].
const undoOf = (before) => (before.status === "approved" ? ["approved"] : before.status === "needs_new" ? ["needs_new", before.reason] : ["clear"]);

function DocumentRow({ doc, requestId, travellerName, partner, letterKeys, canEdit, ready, onChanged, onApproved, onReviewed, nextName, openToken, onEmail, emailLabel, facts, because }) {
  const dispatch = useDispatch();
  const [viewing, setViewing] = useState(false);
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState("");
  const [busy, setBusy] = useState(null);
  const busyRef = useRef(false);
  const rowRef = useRef(null);
  const reasonRef = useRef(null);
  const upload = doc.upload;
  const canReview = canEdit && ready && !!upload;
  const canApprove = canReview && upload.review_status !== "approved";
  const canAsk = canReview && upload.review_status !== "needs_new";
  // Marked for a new copy and the customer not told yet.
  const toEmail = canReview && upload.review_status === "needs_new" && !upload.requested_at && !!onEmail;

  // The viewer opens here when the agent approved the document before this one.
  useEffect(() => {
    if (openToken) setViewing(true);
  }, [openToken]);

  // The preview is fetched only once the row is near the screen (or hovered),
  // and its blob URL is released when the row goes away.
  const image = !!upload && IMAGE_FILE.test(upload.file);
  const aiCheck = aiCheckView(upload);
  const [wanted, setWanted] = useState(false);
  const { ref: inViewRef, inView } = useInView({ triggerOnce: true, rootMargin: "200px 0px", skip: !image, fallbackInView: true });
  const active = image && (inView || wanted);
  const [preview, setPreview] = useState({});
  const file = upload?.file;
  useEffect(() => {
    if (!active || !file) return undefined;
    let alive = true;
    let url = null;
    openPrivateFile(file)
      .then((result) => {
        if (!alive || !result.type.startsWith("image/")) {
          URL.revokeObjectURL(result.url);
          if (alive) setPreview({ notImage: true });
          return;
        }
        url = result.url;
        setPreview({ url: result.url, type: result.type });
      })
      .catch(() => alive && setPreview({ failed: true }));
    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [active, file]);

  // "Passport First Page (Zz Tester)": which document, and whose.
  const named = `${doc.name}${travellerName ? ` (${travellerName})` : ""}`;
  const uploadId = upload?.id;

  // Every decision says what it did, with Undo, and goes on the timeline
  // (the API records it).
  const undo = async (before) => {
    try {
      const [decision, text] = undoOf(before);
      await casesApi.review(uploadId, decision, text, dispatch);
      toast.info(
        before.status === "approved" ? `Approved again: ${named}` : before.status === "needs_new" ? `Back to “New copy needed, not sent yet”: ${named}` : `Undone: ${named} is back to “To review”`,
      );
    } catch {
      /* the server's message says why */
    }
    onChanged();
  };

  // fromViewer: the viewer moves on itself ("Approve, then next"); from the
  // row, the keyboard goes to the next document still to review
  // (CMS review round 2, nadia-12).
  const review = async (decision, text, { fromViewer = false } = {}) => {
    if (busyRef.current) return false;
    busyRef.current = true;
    setBusy(decision);
    const before = { status: upload.review_status || null, reason: upload.review_reason || null };
    try {
      await casesApi.review(upload.id, decision, text, dispatch);
      setAsking(false);
      setReason("");
      onChanged();
      toast.success(<UndoToast text={decisionWords(decision, named)} onUndo={() => undo(before)} />, { autoClose: 20000, closeOnClick: false });
      if (!fromViewer && decision !== "clear") onReviewed?.(doc.document_id);
      return true;
    } catch {
      /* the toast already says why; the row keeps what it showed */
      return false;
    } finally {
      busyRef.current = false;
      setBusy(null);
    }
  };

  const openAsk = () => {
    setReasonError("");
    // Starts from what the AI check found, for the agent to edit.
    if (!reason.trim() && aiCheck?.reason) setReason(aiCheck.reason);
    setAsking(true);
    setTimeout(() => reasonRef.current?.focus(), 0);
  };

  const closeAsk = () => {
    setAsking(false);
    setReasonError("");
    rowRef.current?.focus();
  };

  const submitAsk = () => {
    if (reason.trim().length < 3) {
      setReasonError("Say what is wrong, so the customer knows what to send.");
      reasonRef.current?.focus();
      return undefined;
    }
    return review("needs_new", reason.trim());
  };

  const onKeyDown = (event) => {
    if (event.target !== event.currentTarget || hasModifier(event)) return;
    const key = event.key.toLowerCase();
    // A and N change the review: only when the agent switched letter
    // shortcuts on. Viewing and moving are always there.
    if (key === "a" && canApprove && letterKeys) {
      event.preventDefault();
      review("approved");
    } else if (key === "n" && canAsk && letterKeys) {
      event.preventDefault();
      openAsk();
    } else if ((key === "v" || key === "enter") && upload) {
      event.preventDefault();
      setViewing(true);
    } else if (key === "arrowdown" || key === "arrowup") {
      event.preventDefault();
      focusSibling(event.currentTarget, key === "arrowdown" ? 1 : -1);
    }
  };

  const setRowRef = (node) => {
    rowRef.current = node;
    inViewRef(node);
  };

  return (
    <Box
      ref={setRowRef}
      id={docAnchor(requestId, doc.document_id)}
      data-doc-row
      tabIndex={0}
      role="group"
      aria-label={`${named}${upload ? "" : ", not uploaded"}`}
      aria-keyshortcuts={canReview && letterKeys ? "A N V" : upload ? "V" : undefined}
      onKeyDown={onKeyDown}
      onMouseEnter={() => image && setWanted(true)}
      sx={{
        py: 1.5,
        px: 1,
        mx: -1,
        borderBottom: 1,
        borderColor: "divider",
        borderRadius: 1,
        scrollMarginTop: 160,
        outline: "none",
        "&:focus-visible": { boxShadow: (t) => `inset 0 0 0 2px ${t.palette.primary.main}`, bgcolor: "primary.light" },
      }}
    >
      {/* The text keeps at least 260px; the buttons wrap under it rather
          than squeeze it to a word per line (QA nadia-05). */}
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", columnGap: 1.5, rowGap: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ flex: "1 1 320px", minWidth: 0 }}>
          <Thumbnail upload={upload} preview={preview} name={doc.name} onOpen={() => setViewing(true)} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography fontWeight={600} sx={{ overflowWrap: "anywhere" }}>
                {doc.name}
              </Typography>
              {!doc.required && <Chip size="small" variant="outlined" label="Optional" sx={chipSx} />}
              {doc.copy_kind ? <Chip size="small" variant="outlined" label={`Give ${COPY_TEXT[doc.copy_kind]}`} sx={chipSx} /> : null}
            </Stack>
            {/* The state on its own line, the same in every state: a long
                "New copy asked …" no longer squeezes the row (nadia-13). */}
            <Box sx={{ mt: 0.5 }}>
              <ReviewChip upload={upload} partner={partner} />
            </Box>
            {because && (
              <Typography variant="body2" color="text.secondary">
                {because}
              </Typography>
            )}
            {doc.note && (
              <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                <Box component="span" sx={{ fontWeight: 600 }}>
                  For this visa:
                </Box>{" "}
                {doc.note}
              </Typography>
            )}
            {upload?.expire_at && (
              <Typography variant="body2" color="text.secondary">
                Valid until {dateLong(upload.expire_at)}
                {doc.validity ? ` · must be ${validityText(doc.validity)}` : doc.min_valid_months ? ` · needs ${doc.min_valid_months} ${doc.min_valid_months === 1 ? "month" : "months"} left` : ""}
              </Typography>
            )}
            {/* Used again from one of the customer's earlier applications (SA-09). */}
            {upload?.reused_from && (
              <Typography variant="body2" color="text.secondary">
                Used again from the customer’s application{" "}
                <Link component={RouterLink} to={`/cases/${upload.reused_from}`} sx={{ fontWeight: 600 }}>
                  #{upload.reused_from}
                </Link>
              </Typography>
            )}
            {upload?.review_status === "needs_new" && upload.review_reason && (
              <Typography variant="body2" color="warning.dark" sx={{ overflowWrap: "anywhere" }}>
                What the {partner ? "traveller" : "customer"} is told: {upload.review_reason}
              </Typography>
            )}
            {!upload && doc.required && (
              <Typography variant="body2" color="warning.dark">
                Required, not uploaded yet
              </Typography>
            )}
          </Box>
        </Stack>
        {upload && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ flex: "0 1 auto", ml: { xs: 0, sm: "auto" }, alignSelf: "center" }}>
            {/* On a phone the decisions come first; View and Undo follow.
                Each name says which document, and whose (QA nadia-25).
                Beside Approve, a bare "Undo" read as undoing an approval:
                on a new copy request it says what it takes back, a
                little apart (round 3, nadia-14). */}
            <ActionButton variant="text" startIcon={<IconEye size={16} />} onClick={() => setViewing(true)} aria-label={`View ${named}`} sx={{ order: { xs: 3, sm: 0 } }}>
              View
            </ActionButton>
            {canApprove && (
              <ActionButton
                startIcon={<IconCheck size={16} />}
                busy={busy === "approved"}
                busyLabel="Approving…"
                disabled={!!busy && busy !== "approved"}
                onClick={() => review("approved")}
                aria-label={`Approve ${named}`}
              >
                Approve
              </ActionButton>
            )}
            {canAsk && (
              <ActionButton startIcon={<IconRefresh size={16} />} disabled={!!busy} onClick={openAsk} aria-label={`Ask for a new copy of ${named}`}>
                Ask for a new copy
              </ActionButton>
            )}
            {toEmail && (
              <ActionButton variant="contained" startIcon={<IconMail size={16} />} onClick={onEmail}>
                {emailLabel}
              </ActionButton>
            )}
            {canReview && upload.review_status && (
              <ActionButton
                variant="text"
                busy={busy === "clear"}
                busyLabel={upload.review_status === "approved" ? "Undoing…" : "Taking it back…"}
                disabled={!!busy && busy !== "clear"}
                onClick={() => review("clear")}
                aria-label={upload.review_status === "approved" ? `Undo the approval of ${named}` : `Take back the new copy request for ${named}`}
                sx={{ order: { xs: 4, sm: 0 }, ml: { xs: 0, sm: upload.review_status === "needs_new" ? 1.5 : 0 } }}
              >
                {upload.review_status === "approved" ? "Undo" : "Take back “new copy”"}
              </ActionButton>
            )}
          </Stack>
        )}
      </Box>

      {aiCheck && <AiCheckNote view={aiCheck} />}
      {asking && (
        <Box
          mt={1.5}
          p={1.5}
          sx={{ bgcolor: "warning.light", borderRadius: 2 }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.stopPropagation();
              closeAsk();
            }
          }}
        >
          <Typography variant="body2" fontWeight={600} mb={1}>
            What is wrong with {named}? The {partner ? "traveller" : "customer"} reads this.
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap mb={1}>
            {QUICK_REASONS.map((r) => (
              <Chip
                key={r}
                size="small"
                label={r}
                onClick={() => {
                  setReason(r);
                  setReasonError("");
                }}
                variant={reason === r ? "filled" : "outlined"}
                color={reason === r ? "primary" : "default"}
                sx={{ bgcolor: reason === r ? undefined : "background.paper" }}
              />
            ))}
          </Stack>
          <TextField
            fullWidth
            size="small"
            value={reason}
            inputRef={reasonRef}
            onChange={(e) => {
              setReason(e.target.value);
              if (reasonError) setReasonError("");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                submitAsk();
              }
            }}
            error={!!reasonError}
            helperText={reasonError || " "}
            placeholder="For example: the passport photo page is cut off at the bottom"
            inputProps={{ maxLength: 500, "aria-label": "Reason for a new copy" }}
            sx={{ "& .MuiInputBase-root": { bgcolor: "background.paper" } }}
          />
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <ActionButton variant="text" onClick={closeAsk} disabled={!!busy}>
              Keep as it is
            </ActionButton>
            <ActionButton variant="contained" busy={busy === "needs_new"} busyLabel="Saving…" onClick={submitAsk}>
              Mark as needing a new copy
            </ActionButton>
          </Stack>
        </Box>
      )}
      {viewing && (
        <FileViewer
          file={upload.file}
          title={named}
          preloaded={preview.url ? preview : null}
          facts={facts}
          onClose={() => setViewing(false)}
          approveLabel={nextName ? "Approve, then next" : "Approve"}
          onApprove={
            canApprove
              ? async () => {
                  if (await review("approved", undefined, { fromViewer: true })) {
                    setViewing(false);
                    onApproved?.(doc.document_id);
                  }
                }
              : null
          }
          emailLabel={emailLabel}
          onEmail={
            toEmail
              ? () => {
                  setViewing(false);
                  onEmail();
                }
              : null
          }
          onAskNew={
            canAsk
              ? () => {
                  setViewing(false);
                  openAsk();
                }
              : null
          }
        />
      )}
    </Box>
  );
}

DocumentRow.propTypes = {
  onReviewed: PropTypes.func,
  doc: PropTypes.object.isRequired,
  requestId: PropTypes.number,
  travellerName: PropTypes.string,
  partner: PropTypes.string,
  letterKeys: PropTypes.bool,
  canEdit: PropTypes.bool,
  ready: PropTypes.bool,
  onChanged: PropTypes.func.isRequired,
  onApproved: PropTypes.func,
  nextName: PropTypes.string,
  openToken: PropTypes.number,
  onEmail: PropTypes.func,
  emailLabel: PropTypes.string,
  facts: PropTypes.array,
  // Why this traveller is asked for it, when the product asks only some.
  because: PropTypes.string,
};

const Key = ({ children }) => (
  <Box
    component="kbd"
    sx={{
      fontFamily: "inherit",
      fontSize: "0.75rem",
      fontWeight: 600,
      px: 0.6,
      py: 0.1,
      mx: 0.25,
      border: 1,
      borderColor: "divider",
      borderBottomWidth: 2,
      borderRadius: 0.75,
      bgcolor: "background.paper",
    }}
  >
    {children}
  </Box>
);

Key.propTypes = { children: PropTypes.node };

// emailLabel: what sends the new-copy request ("Send to <partner> now" on a
// partner's case).
// travellerName names the traveller in each button and toast ("Approve
// Passport First Page (Zz Tester)").
export default function Documents({ traveller, travellerName, partner, canEdit, ready, onChanged, onEmail, emailLabel = "Email the customer now", open }) {
  const reviewable = canEdit && ready && traveller.documents.some((d) => d.upload);
  const [openNext, setOpenNext] = useState(null);
  const [letterKeys, setLetterKeys] = useState(readLetterKeys);
  // "Review the first" from the case's banner opens that document's viewer.
  useEffect(() => {
    if (open?.documentId) setOpenNext(open);
  }, [open]);
  const facts = passportFacts(traveller);
  // The traveller's answer to the question a document depends on.
  const answerTo = (questionId) =>
    traveller.sections.flatMap((s) => s.fields).find((f) => Number(f.question_id) === Number(questionId))?.value;
  // The next document still to review after this one (then from the top).
  const nextPending = (documentId) => {
    const docs = traveller.documents;
    const at = docs.findIndex((d) => d.document_id === documentId);
    return [...docs.slice(at + 1), ...docs.slice(0, at)].find((d) => d.upload && !d.upload.review_status) || null;
  };
  return (
    <Box>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "center" }} spacing={0.5} mb={0.5}>
        <Typography variant="h6" component="h3">
          Documents
        </Typography>
        {reviewable && (
          // Off until switched on, on this computer: a stray key never
          // approves a document (QA nadia-11, georges-18).
          <Stack direction="row" alignItems="center" columnGap={1.5} rowGap={0.5} flexWrap="wrap" sx={{ display: { xs: "none", md: "flex" } }}>
            <Typography variant="body2" color="text.secondary">
              {letterKeys ? (
                <>
                  Tab to a document, then <Key>A</Key> approve · <Key>N</Key> new copy · <Key>V</Key> view · <Key>↑</Key>
                  <Key>↓</Key> move
                </>
              ) : (
                <>
                  Tab to a document, then <Key>V</Key> view · <Key>↑</Key>
                  <Key>↓</Key> move
                </>
              )}
            </Typography>
            <FormControlLabel
              sx={{ mr: 0, "& .MuiFormControlLabel-label": { pl: 0.5 } }}
              control={
                <Switch
                  size="small"
                  checked={letterKeys}
                  onChange={(e) => {
                    setLetterKeys(e.target.checked);
                    saveLetterKeys(e.target.checked);
                  }}
                />
              }
              label={<Typography variant="body2">Letter shortcuts A and N</Typography>}
            />
          </Stack>
        )}
      </Stack>
      {traveller.documents.length === 0 && <Typography color="text.secondary">This visa asks for no documents.</Typography>}
      <Box data-doc-list>
        {traveller.documents.map((doc) => (
          <DocumentRow
            key={`${doc.document_id}-${doc.upload?.id ?? "none"}`}
            doc={doc}
            requestId={traveller.request_id}
            travellerName={travellerName}
            partner={partner}
            letterKeys={letterKeys}
            canEdit={canEdit}
            ready={ready}
            onChanged={onChanged}
            onEmail={onEmail}
            emailLabel={emailLabel}
            facts={isPassportDocument(doc) ? facts : undefined}
            because={askedBecause(doc.show_when, answerTo(doc.show_when?.question_id)) ?? undefined}
            nextName={reviewable ? nextPending(doc.document_id)?.name : undefined}
            openToken={openNext?.documentId === doc.document_id ? openNext.n : 0}
            onApproved={(id) => {
              const next = nextPending(id);
              if (next) setOpenNext({ documentId: next.document_id, n: Date.now() });
            }}
            onReviewed={(id) => {
              const next = nextPending(id);
              if (next) setTimeout(() => document.getElementById(docAnchor(traveller.request_id, next.document_id))?.focus(), 0);
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

Documents.propTypes = {
  traveller: PropTypes.object.isRequired,
  travellerName: PropTypes.string,
  partner: PropTypes.string,
  canEdit: PropTypes.bool,
  ready: PropTypes.bool,
  onChanged: PropTypes.func.isRequired,
  onEmail: PropTypes.func,
  emailLabel: PropTypes.string,
  open: PropTypes.object,
};
